import React, { FC, useState, useMemo } from "react";
import SearchInput from "./SearchInput";
import OptionGrid from "./OptionGrid";
import NoSearchResults from "./NoSearchResults";
import styles from "../../../styles/B2C/StepLayout.module.scss";
import { useWizardOptions } from "../../../context/WizardOptionsContext";

// Show only the first N offices (in response order) until the user searches;
// a search query then filters the full list.
const DEFAULT_VISIBLE_COUNT = 20;

interface NearestOfficeStepProps {
  selected: string;
  onSelect: (office: string) => void;
}

const NearestOfficeStep: FC<NearestOfficeStepProps> = ({
  selected,
  onSelect,
}) => {
  const { nearestOffices } = useWizardOptions();
  const options = useMemo(
    () => (nearestOffices ?? []).map((office) => office.label),
    [nearestOffices]
  );

  const [search, setSearch] = useState("");

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options.slice(0, DEFAULT_VISIBLE_COUNT);
    return options.filter((label) =>
      label.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, options]);

  const handleSelect = (office: string) => {
    onSelect(office);
  };

  // CFM-1514: dynamic count — round down to nearest 10 + "+" (fallback while loading)
  const officeCountLabel =
    options.length >= 10 ? `${Math.floor(options.length / 10) * 10}+` : "60+";

  return (
    <div className={styles.stepContent}>
      <SearchInput value={search} onChange={setSearch} placeholder="Search..." sticky />
      {/* CFM-1514: "Nearest offices" hint shown only in the default (non-search) view */}
      {!search.trim() && (
        <div className={styles.topCities}>
          <h3 className={styles.topCitiesHeading}>Nearest offices</h3>
          <p className={styles.topCitiesHelper}>
            Don&apos;t see your nearest office?{" "}
            <span className={styles.topCitiesHelperStrong}>Search above</span> - we
            have 60+ offices.
          </p>
        </div>
      )}
      {search.trim() !== "" && filteredOptions.length === 0 ? (
        <NoSearchResults />
      ) : (
        <OptionGrid
          options={filteredOptions}
          selected={selected ? [selected] : []}
          onSelect={handleSelect}
          multiSelect={false}
        />
      )}
    </div>
  );
};

export default NearestOfficeStep;
